<template>
  <div>
    <div class="main">
      <button
        v-if="!isChatbotVisible"
        class="floating-button"
        :aria-label="tStatic('caiAccessibilityOpenChatInfoText')"
        :title="tStatic('caiFloatingButtonText')"
        @click="toggleChatbot">
        <span v-if="hasUnreadMessages" class="unread-indicator" aria-hidden="true"></span>
        <Chat width="28" height="28"></Chat>
        <div class="floating-button__label">
          {{ tStatic('caiFloatingButtonText') }}
        </div>
      </button>

      <div
        v-if="isChatbotVisible"
        class="chatbot"
        role="dialog"
        aria-labelledby="chatbot-header"
        @keydown="handleEscapeKeyOnChatbot">
        <div id="chatbot-header" class="header">
          <div class="logo-title-wrapper">
            <h5 id="chatbot-title">
              {{ tStatic('caiTitle') }}
            </h5>
          </div>
          <div class="actions">
            <button
              type="button"
              :aria-label="tStatic('caiAccessibilityMinimizeChatInfoText')"
              :title="tStatic('genericMinimize')"
              @click="toggleChatbot">
              <Minimize width="20" height="20"></Minimize>
            </button>
            <button
              type="button"
              :aria-label="tStatic('caiAccessibilityCloseChatInfoText')"
              :title="tStatic('genericClose')"
              @click="handleClose">
              <oie-use-svg
                svg="close-cross"
                color="#fff"
                width="18"
                height="18">
              </oie-use-svg>
            </button>
          </div>
        </div>

        <div ref="messageContainer" class="body" :style="bodyBackgroundStyle">
          <oie-clear-chat-bot-popup
            :is-visible="isClearChatDialogVisible"
            :handle-cancel="handleCancel"
            :handle-clear="handleClear">
          </oie-clear-chat-bot-popup>
          <oie-session-timeout-popup
            :is-visible="isSessionTimeoutVisible"
            :countdown="sessionTimeoutCountdown">
          </oie-session-timeout-popup>
          <oie-session-expired-popup
            :is-visible="isSessionExpiredPopupVisible"
            :on-ok="handleSessionExpiredOk">
          </oie-session-expired-popup>
          <div
            v-for="(message, index) in messages"
            :key="`msg_${index}`"
            :ref="`message_${index}`"
            class="message-container">
            <template v-if="message.sender === 'bot'">
              <div
                v-if="message.isTyping"
                class="message response loading-text"
                aria-live="polite">
                <span class="typing-dots" aria-hidden="true">
                  <span class="dot"></span>
                  <span class="dot"></span>
                  <span class="dot"></span>
                </span>
                <span class="typing-text">{{ displayedText }}<span v-if="isTyping" class="typing-cursor">|</span></span>
              </div>
              <div v-else class="message response">
                <div aria-live="polite" v-html="sanitizeHtml(message.text)"></div>
                <template v-for="(option, optionIndex) in message.options">
                  <div v-if="option" :key="`option_${optionIndex}`">
                    <div v-if="option.entityType === 1" class="message-options">
                      <oie-product-item-option
                        v-for="(entity, entityIndex) in option.entities"
                        :key="`entity_${entityIndex}`"
                        :session-id="sessionId"
                        :exchange-id="message.exchangeId"
                        :name="entity.name"
                        :price="entity.lowestPrice"
                        :currency="entity.currency"
                        :region="entity.region"
                        :vat-included="entity.vatIncluded"
                        :items="entity.items">
                      </oie-product-item-option>
                    </div>
                    <div v-else-if="option.entityType === 2" class="message-options">
                      <oie-webcontent-option
                        v-for="(entity, entityIndex) in option.entities"
                        :key="`entity_${entityIndex}`"
                        :session-id="sessionId"
                        :title="entity.title"
                        :url="entity.url"
                        aria-label="Web content option">
                      </oie-webcontent-option>
                    </div>
                    <div v-else-if="option.entityType === 3" class="message-options">
                      <oie-order-option
                        v-for="(entity, entityIndex) in option.entities"
                        :key="`entity_${entityIndex}`"
                        :session-id="sessionId"
                        :number="entity.number"
                        :date="entity.date"
                        :display-date="entity.displayDate"
                        :status-key="entity.statusKey"
                        :total="entity.total"
                        :currency="entity.currency"
                        :goods-information-no="entity.goodsInformationNo"
                        :shipment-id="entity.shipmentId"
                        :tracking-url="entity.trackingUrl"
                        :invoices="entity.invoices">
                      </oie-order-option>
                    </div>
                  </div>
                </template>
                <span v-if="message.timestamp" class="message-timestamp">{{ formatTimestamp(message.timestamp) }}</span>
              </div>
            </template>
            <template v-else>
              <div class="message prompt" aria-live="polite" v-text="message.text"></div>
              <span v-if="message.timestamp" class="message-timestamp message-timestamp--right">{{ formatTimestamp(message.timestamp) }}</span>
            </template>
            <div v-if="index === 0 && !sessionId" class="conversation-starters">
              <button
                v-for="action in conversationStarters"
                :key="action"
                class="conversation-starter-btn"
                @click="sendConversationStarter(action)">
                {{ action }}
              </button>
            </div>
          </div>
        </div>

        <div class="footer">
          <div v-if="tStatic('caiShortDisclaimer') !== null || tStatic('caiLongDisclaimer') !== null" class="disclaimer">
            <span v-if="!showLongDisclaimer">
              {{ tStatic('caiShortDisclaimer') }}
              <button class="disclaimer-toggle" :aria-label="tStatic('caiAccessibilityDisclaimerReadMoreToggleText')" @click="toggleDisclaimer">
                {{ tStatic('genericReadMore') }}
              </button>
            </span>
            <span v-else>
              {{ tStatic('caiLongDisclaimer') }}
              <button class="disclaimer-toggle" :aria-label="tStatic('caiAccessibilityDisclaimerShowLessToggleText')" @click="toggleDisclaimer">
                {{ tStatic('genericShowLess') }}
              </button>
            </span>
          </div>
          <div class="input-field">
            <button
              type="button"
              class="quick-actions-toggle"
              :aria-label="showQuickActions ? tStatic('genericCollapse') : tStatic('genericExpand')"
              :title="showQuickActions ? tStatic('genericCollapse') : tStatic('genericExpand')"
              @click="showQuickActions = !showQuickActions">
              <ChevronDown v-if="!showQuickActions" width="25" height="25"></ChevronDown>
              <ChevronUp v-else width="25" height="25"></ChevronUp>
            </button>
            <label for="chat-input" class="visually-hidden">{{ tStatic('caiInputPlaceholder') }}</label>
            <div class="textarea-wrapper">
              <textarea
                id="chat-input"
                ref="text-area"
                v-model="newMessage"
                type="text"
                class="message-input"
                :placeholder="tStatic('caiInputPlaceholder')"
                :disabled="isTextFieldDisable"
                aria-label="Chat input"
                @keydown.exact.enter.prevent="sendMessage">
              </textarea>
              <oie-action-link
                class="send-button"
                :disabled="isTextFieldDisable"
                :aria-label="tStatic('caiAccessibilitySendMessageInfoText')"
                :title="tStatic('genericSend')"
                @click="sendMessage">
                <Send width="30" height="30" :style="{ color: focusColor }"></Send>
              </oie-action-link>
            </div>
          </div>
          <transition name="quick-actions-slide">
            <div v-if="showQuickActions" class="quick-actions">
              <a
                v-if="tStatic('caiQuickActionFaqTitle') && tStatic('caiQuickActionFaqLink')"
                :href="tStatic('caiQuickActionFaqLink')"
                class="quick-action-btn">
                <Help width="25" height="25"></Help>
                {{ tStatic('caiQuickActionFaqTitle') }}
              </a>
              <a
                v-if="tStatic('caiQuickActionContactUsTitle') && tStatic('caiQuickActionContactUsLink')"
                :href="tStatic('caiQuickActionContactUsLink')"
                class="quick-action-btn">
                <Phone width="25" height="25"></Phone>
                {{ tStatic('caiQuickActionContactUsTitle') }}
              </a>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import httpService from '@/core/http-service'
import { v4 as uuidv4 } from 'uuid'
import gtm from '@/core/enhanced-ecommerce-gtm'
import { mapGetters, mapActions } from 'vuex'
import { chatEventBus } from '@/store/chat'
import { sanitizeChatHtml } from '@/core/html-sanitizer'

const DEFAULT_SESSION_TIMEOUT_SECONDS = 12 * 60 * 60 // 12 hours
const SESSION_TIMEOUT_COUNTDOWN_SECONDS = 5
const INACTIVITY_CHECK_INTERVAL_MS = 30 * 1000 // check every 30 seconds

export default {
  data() {
    return {
      isChatbotVisible: false,
      isChatbotFocused: false,
      isClearChatDialogVisible: false,
      isSessionTimeoutVisible: false,
      sessionTimeoutCountdown: SESSION_TIMEOUT_COUNTDOWN_SECONDS,
      isSessionExpiredPopupVisible: false,
      isTextFieldDisable: false,
      showLongDisclaimer: false,
      showQuickActions: true,
      newMessage: '',
      focusColor: '#ccc',
      cancelTokenSource: null,
      hasUnreadMessages: false,
      conversationStarters: [...(this.tStatic('caiPromptShortcutsList') || [])],
      thinkingTexts: [this.tStatic('caiRespondingTextStage1'), this.tStatic('caiRespondingTextStage2'), this.tStatic('caiRespondingTextStage3')],
      currentThinkingTextIndex: 0,
      thinkingInterval: null,
      displayedText: '',
      isTyping: false,
      typingSpeed: 50,
      typingTimeout: null,
      inactivityCheckInterval: null,
      countdownInterval: null
    }
  },
  computed: {
    ...mapGetters('chat', ['messages', 'username', 'sessionId']),
    backgroundImageUrl() {
      return this.getAssetPath(this.clientModel.caiBackgroundImageUrl)
    },
    sessionTimeoutSeconds() {
      return (this.clientModel.caiSessionTimeoutDuration * 60) || DEFAULT_SESSION_TIMEOUT_SECONDS
    },
    bodyBackgroundStyle() {
      if (!this.sessionId) {
        return {
          backgroundImage: `url(${this.backgroundImageUrl})`,
          backgroundSize: '110px',
          backgroundPosition: 'right 10px bottom',
          backgroundRepeat: 'no-repeat'
        }
      }
      return {}
    }
  },
  watch: {
    // Start/stop inactivity timer whenever the session changes
    sessionId(newVal) {
      if (newVal) {
        this.startInactivityTimer()
      } else {
        this.stopInactivityTimer()
        this.clearCountdownInterval()
        this.isSessionTimeoutVisible = false
      }
    }
  },
  beforeCreate() {
    this.cancelToken = null
  },
  mounted() {
    // Listen for minimize events from the store
    chatEventBus.$on('minimize-chatbot', this.minimizeChatbot)
    chatEventBus.$on('session-ended', this.onSessionEnded)

    this.subscribe('chat.open', (message) => {
      if (!this.isChatbotVisible) {
        this.toggleChatbot()
      }
      if (message) {
        this.$nextTick(() => this.sendConversationStarter(message))
      }
    })

    // Clean up any persisted typing message from previous session
    this.removeTypingMessage()

    // Sync with localStorage sessionId if it exists but store doesn't have it
    const localSessionId = localStorage.getItem('sessionId')
    if (localSessionId && !this.sessionId) {
      this.setSessionId(localSessionId)
    }

    // On first page load (browser reopen scenario): clear any session that has
    // exceeded the inactivity threshold before the user can interact.
    if (this.sessionId && this.isSessionExpired()) {
      this.clearChatHistory()
    }

    // Reset chat when there's no active session to ensure the greeting
    // uses the correct market language (the store's initial state is
    // evaluated at import time, before localization data is loaded)
    if (!this.sessionId) {
      this.resetChat()
    }

    // Begin watching for inactivity if a session is already active
    if (this.sessionId) {
      this.startInactivityTimer()
    }

    this.showQuickActions = !this.sessionId

    // Add beforeunload listener to clean up typing messages
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  updated() {
    if(this.newMessage !== '') {
      this.focusColor = '#0073bb'
    } else {
      this.focusColor = '#ccc'
    }
  },
  beforeDestroy() {
    this.stopThinkingTextCycle()
    this.stopInactivityTimer()
    this.clearCountdownInterval()

    // Remove event listener
    chatEventBus.$off('minimize-chatbot', this.minimizeChatbot)
    chatEventBus.$off('session-ended', this.onSessionEnded)

    // Clean up typing message before navigating away
    this.removeTypingMessage()

    // Cancel any pending requests
    if (this.cancelToken) {
      this.cancelToken()
    }

    // Remove beforeunload listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    ...mapActions('chat', ['addMessage', 'addUserMessage', 'addBotMessage', 'clearMessages', 'resetChat', 'setSessionId', 'setOwner']),
    // Bot replies are markdown rendered to HTML. They are sanitized server side,
    // but messages are also restored from local storage, so sanitize again here
    // before handing anything to v-html.
    sanitizeHtml(html) {
      return sanitizeChatHtml(html)
    },
    minimizeChatbot() {
      this.isChatbotVisible = false
      // If the timeout popup was visible when the user minimizes, clear silently
      if (this.isSessionTimeoutVisible) {
        this.clearCountdownInterval()
        this.isSessionTimeoutVisible = false
        this.clearChatHistory()
      }
    },
    startThinkingTextCycle() {
      this.currentThinkingTextIndex = 0
      this.stopThinkingTextCycle()

      this.typeText(this.thinkingTexts[0])

      const advanceToNextText = (index) => {
        if (index < this.thinkingTexts.length - 1) {
          this.thinkingInterval = setTimeout(() => {
            this.currentThinkingTextIndex = index + 1
            this.typeText(this.thinkingTexts[index + 1])
            advanceToNextText(index + 1)
          }, 10000)
        }
      }

      advanceToNextText(0)
    },
    typeText(text) {
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout)
        this.typingTimeout = null
      }

      this.isTyping = true
      this.displayedText = ''

      const typeNextChar = (index) => {
        if (index <= text.length) {
          this.displayedText = text.substring(0, index)
          this.typingTimeout = setTimeout(() => {
            typeNextChar(index + 1)
          }, this.typingSpeed)
        } else {
          this.isTyping = false
          this.typingTimeout = null
        }
      }

      typeNextChar(0)
    },
    stopThinkingTextCycle() {
      if (this.thinkingInterval) {
        clearTimeout(this.thinkingInterval)
        this.thinkingInterval = null
      }
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout)
        this.typingTimeout = null
      }
      this.currentThinkingTextIndex = 0
      this.isTyping = false
      this.displayedText = ''
    },
    toggleDisclaimer() {
      this.showLongDisclaimer = !this.showLongDisclaimer
    },
    toggleChatbot() {
      this.isChatbotVisible = !this.isChatbotVisible
      if (this.isChatbotVisible) {
        this.scrollToBottom()
        this.hasUnreadMessages = false
        this.$nextTick(() => {
          this.$refs['text-area'].focus()
        })
      } else if (this.isSessionTimeoutVisible) {
        // User minimized while timeout popup was open — clear silently
        this.clearCountdownInterval()
        this.isSessionTimeoutVisible = false
        this.clearChatHistory()
      }
    },
    handleClose() {
      if (this.messages.length <= 1) {
        this.isChatbotVisible = false
        return
      }

      this.isClearChatDialogVisible = true
    },
    handleCancel() {
      this.isClearChatDialogVisible = false
    },
    handleClear() {
      if (this.cancelToken) {
        this.cancelToken()
      }

      this.$store.dispatch('chat/trackSessionEnd', 'clear')
      this.clearChatHistory()
      this.isChatbotVisible = false
      this.isClearChatDialogVisible = false
    },
    handleEscapeKeyOnChatbot(event) {
      if(event.key === 'Escape' || event.keyCode === 27) {
        this.isChatbotVisible = false
      }
    },
    async send(requestBody) {
      try {
        this.cancelToken = httpService.createCancellationToken()
        const response = await httpService.post(
          'cai/chat',
          requestBody,
          {},
          {},
          this.cancelToken.cancelTokenSource
        )
        return { result: response.data, isCancelled: false, isSessionGone: false }
      } catch (error) {
        console.log('An error occurred: ' + error)
        const isSessionGone = error.response && error.response.status === 410
        return { result: null, isCancelled: httpService.isCancellationError(error), isSessionGone }
      } finally {
        this.cancelToken = null
      }
    },
    async sendMessage() {
      if (this.newMessage.trim() === '') {
        this.$refs['text-area'].value = ''
        return
      }

      this.isTextFieldDisable = true

      let exchangeId = uuidv4()
      let sessionId = this.sessionId || localStorage.getItem('sessionId')

      if (sessionId === null) {
        sessionId = this.createSessionId()
        this.setSessionId(sessionId)
        this.setOwnerFromClientModel()
        gtm.chatSessionStart(sessionId)
        this.showQuickActions = false
      }

      // Record this message as the latest activity for inactivity tracking
      this.updateLastActivity()

      this.addUserMessage({ text: this.newMessage, exchangeId: exchangeId })
      this.scrollToBottom()

      // Add typing message with exchangeId for easier identification
      this.addBotMessage({ text: '', isTyping: true, exchangeId: exchangeId })
      this.startThinkingTextCycle()
      this.scrollToBottom()

      const requestBody = {
        sessionId: sessionId,
        exchangeId: exchangeId,
        message: this.newMessage
      }

      try {
        const botResponse = await this.send(requestBody)
        await gtm.chatPrompt(this.sessionId, this.newMessage)

        // Always remove typing message before processing response
        this.removeTypingMessage()
        this.stopThinkingTextCycle()
        this.newMessage = ''

        if (botResponse.result) {
          this.handleSuccessResponse(botResponse.result, this.sessionId, exchangeId)
        } else if (botResponse.isSessionGone) {
          const savedMessage = requestBody.message
          // The server already reported the conversation as gone — no need to end it.
          this.clearChatHistory(false)
          this.isSessionExpiredPopupVisible = true

          // Start a new session and resend the message behind the popup
          await this.resendMessage(savedMessage)
          return
        } else if (botResponse.isCancelled) {
          this.clearChatHistory()
          return
        } else {
          const errorMessage = this.tStatic('caiErrorResponse')
          this.addBotMessage({
            text: errorMessage,
            exchangeId: exchangeId
          })
        }
      } catch (error) {
        console.log('An error occurred: ' + error)
        // Ensure typing message is removed even if an error occurs
        this.removeTypingMessage()
        this.newMessage = ''

        const errorMessage = this.tStatic('caiErrorResponse')
        this.addBotMessage({
          text: errorMessage,
          exchangeId: exchangeId
        })
      } finally {
        this.isTextFieldDisable = false
        this.publish('chat.response.complete')
      }

      this.scrollToBottom()

      this.$nextTick(() => {
        this.$refs['text-area'].focus()
      })
    },
    sendConversationStarter(text) {
      this.newMessage = text
      this.sendMessage()
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const lastIndex = this.messages.length - 1

        if (lastIndex < 0) {
          return
        }

        let lastMessage = this.$refs[`message_${lastIndex}`]
        lastMessage = Array.isArray(lastMessage) ? lastMessage[0] : lastMessage

        if (lastMessage) {
          lastMessage.scrollIntoView({ behavior: 'smooth', block: 'start' })
        } else {
          const messageContainer = this.$refs.messageContainer
          if (messageContainer) {
            messageContainer.scrollTop = messageContainer.scrollHeight
          }
        }
      })
    },
    setOwnerFromClientModel() {
      this.setOwner({
        isAuthenticated: this.clientModel.isAuthenticated,
        userName: this.clientModel.user ? this.clientModel.user.userName : null
      })
    },
    createSessionId() {
      const sessionId = uuidv4()
      localStorage.setItem('sessionId', sessionId)

      return sessionId
    },
    clearChatHistory(notifyServer = true) {
      // Intentionally not awaited — callers hide the chat immediately after.
      this.$store.dispatch('chat/endSession', { notifyServer })
    },
    // Runs for every session end, including logout from outside this component
    onSessionEnded() {
      if (this.cancelToken) {
        this.cancelToken()
      }
      this.stopThinkingTextCycle()
      this.clearCountdownInterval()
      this.isSessionTimeoutVisible = false
      this.showQuickActions = true
    },
    removeTypingMessage() {
      // Find and remove the single typing message from the store
      const typingMessageIndex = this.messages.findIndex(message => message.isTyping)
      if (typingMessageIndex !== -1) {
        this.$store.commit('chat/REMOVE_MESSAGE', typingMessageIndex)
      }
    },

    // ─── Inactivity / session-timeout logic ───────────────────────────────────

    updateLastActivity() {
      localStorage.setItem('chatLastActivity', Date.now().toString())
    },

    isSessionExpired() {
      const lastActivity = localStorage.getItem('chatLastActivity')
      if (!lastActivity) return false
      const elapsed = Date.now() - parseInt(lastActivity, 10)
      return elapsed >= this.sessionTimeoutSeconds * 1000
    },

    startInactivityTimer() {
      this.stopInactivityTimer()
      this.inactivityCheckInterval = setInterval(() => {
        if (this.sessionId && this.isSessionExpired()) {
          this.handleSessionExpiry()
        }
      }, INACTIVITY_CHECK_INTERVAL_MS)
    },

    stopInactivityTimer() {
      if (this.inactivityCheckInterval) {
        clearInterval(this.inactivityCheckInterval)
        this.inactivityCheckInterval = null
      }
    },

    handleSessionExpiry() {
      // Stop polling — one-shot response to expiry
      this.stopInactivityTimer()
      if (this.isChatbotVisible) {
        this.showSessionTimeoutPopup()
      } else {
        // Chatbot is minimised — clear silently without any popup
        this.clearChatHistory()
      }
    },

    showSessionTimeoutPopup() {
      this.sessionTimeoutCountdown = SESSION_TIMEOUT_COUNTDOWN_SECONDS
      this.isSessionTimeoutVisible = true
      this.countdownInterval = setInterval(() => {
        this.sessionTimeoutCountdown--
        if (this.sessionTimeoutCountdown <= 0) {
          this.clearCountdownInterval()
          this.isSessionTimeoutVisible = false
          this.clearChatHistory()
        }
      }, 1000)
    },

    clearCountdownInterval() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    },

    handleSessionExpiredOk() {
      this.isSessionExpiredPopupVisible = false
    },

    // ─── Timestamp formatting ──────────────────────────────────────────────────

    formatTimestamp(timestamp) {
      if (!timestamp) return ''
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    // ─── Lifecycle / misc ─────────────────────────────────────────────────────

    handleBeforeUnload() {
      // Clean up typing message when user is about to leave the page
      this.removeTypingMessage()
    },

    // ─── Shared response handling ──────────────────────────────────────────────

    handleSuccessResponse(result, sessionId, exchangeId) {
      const options = result.options.map(entity => ({
        entityType: entity.entityType,
        entities: entity.entities
      }))

      this.addBotMessage({
        text: result.assistantMessage,
        exchangeId: exchangeId,
        options: options
      })

      if (!this.isChatbotVisible) {
        this.hasUnreadMessages = true
      }

      const isProductRecommended = options &&
        options.some(option => option.entityType === 1)

      if (isProductRecommended) {
        gtm.chatResponseOutcome(sessionId, 'product_recommended')
      } else {
        gtm.chatResponseOutcome(sessionId, 'no_recommendation')
      }
    },

    // ─── Resend after 410 (session gone) ────────────────────────────────────────

    async resendMessage(message) {
      // Create a brand-new session
      const newSessionId = this.createSessionId()
      this.setSessionId(newSessionId)
      this.setOwnerFromClientModel()
      gtm.chatSessionStart(newSessionId)

      const newExchangeId = uuidv4()

      this.updateLastActivity()

      // Re-add the user's message so it appears in the new conversation
      this.addUserMessage({ text: message, exchangeId: newExchangeId })
      this.scrollToBottom()

      // Show typing indicator
      this.addBotMessage({ text: '', isTyping: true, exchangeId: newExchangeId })
      this.startThinkingTextCycle()
      this.scrollToBottom()

      const requestBody = {
        sessionId: newSessionId,
        exchangeId: newExchangeId,
        message: message
      }

      try {
        const botResponse = await this.send(requestBody)
        await gtm.chatPrompt(newSessionId, message)

        this.removeTypingMessage()
        this.stopThinkingTextCycle()

        if (botResponse.result) {
          this.handleSuccessResponse(botResponse.result, newSessionId, newExchangeId)
        } else if (botResponse.isCancelled) {
          // Request was cancelled — do not retry
          return
        } else {
          // Covers a second 410 or any other failure — no infinite retry
          const errorMessage = this.tStatic('caiErrorResponse')
          this.addBotMessage({
            text: errorMessage,
            exchangeId: newExchangeId
          })
        }
      } catch (error) {
        console.log('An error occurred during resend: ' + error)
        this.removeTypingMessage()
        this.stopThinkingTextCycle()

        const errorMessage = this.tStatic('caiErrorResponse')
        this.addBotMessage({
          text: errorMessage,
          exchangeId: newExchangeId
        })
      }

      this.scrollToBottom()

      this.$nextTick(() => {
        if (this.$refs['text-area']) {
          this.$refs['text-area'].focus()
        }
      })
    }
  }
}
</script>

<style scoped lang="scss">

.floating-button {
  z-index: 2147483641;
  position: fixed;
  bottom: calc(1.25rem + env(safe-area-inset-bottom));
  right: 1.25rem;
  font-size: 18px;
  background-color: color(button-background-blue-dark);
  color: white;
  border: none;
  border-radius: 9999px;
  padding: 0.625rem 1.25rem;
  gap: 0.5rem;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background-color: color(button-action-active);
  }
}

.unread-indicator {
  position: absolute;
  top: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background-color: red;
  border-radius: 50%;
  border: 2px solid white;
}

.main {
  font-size: 0.875rem;
}

.chatbot {
  display: flex;
  z-index: 2147483641;
  flex-direction: column;
  width: 390px;
  height: calc(100vh - 150px);
  max-height: 700px;
  position: fixed;
  bottom: 20px;
  right: 20px;
  overflow: hidden;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 7px 6px 1px rgba(0, 0, 0, .16);
  animation: fadeInFromBottom 0.3s ease-out;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 60px;
  padding: 0.625rem 1.25rem;
  background-color: #1c508b;
  border: 0;
  color: white;

  i {
    cursor: pointer;
  }

  h5 {
    font-size: 1.25rem;
    margin-bottom: 0;
    font-weight: 700;
  }

  .actions {
    display: flex;
    gap: 10px;

    button {
      display: flex;
      background: none;
      border: none;
      cursor: pointer;
      color: #ffffff;
      border-radius: 5px;
      padding: 4px;
      transition: background-color 0.2s ease, transform 0.1s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }

      &:active {
        background-color: rgba(255, 255, 255, 0.35);
        transform: scale(0.9);
      }

      &:focus-visible {
        outline: 2px solid rgba(255, 255, 255, 0.8);
        outline-offset: 2px;
      }
    }
  }
}

.logo-title-wrapper {
  display: flex;
  align-items: center;

  img {
    border-radius: 12px;
    margin-right: 10px;
  }
}

.body {
  height: 80%;
  padding: 12px;
  background-color: #fff;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  scrollbar-width: auto;
}

.body::-webkit-scrollbar {
  width: 0.625rem;
}

.message-container {
  display: flex;
  flex-direction: column;
}

.message {
  padding: 12px;
  border-radius: 8px;
  text-align: left;
  font-size: 13px;
}

@supports (-webkit-touch-callout: none) {
  .message {
    font-size: 1.125em;
  }
}

.message {
  &.prompt {
    max-width: 80%;
    white-space: pre-wrap;
    overflow-wrap: break-word;
    background-color: #0073bb;
    color: #fff;
    align-self: flex-end;
  }

  &.response {
    background-color: #f2f3f3;
    color: #000;
    align-self: flex-start;
  }
}

.message-options {
  margin-top: 0.625rem;
}

.loading-text {
  position: relative;
  display: flex;
  overflow: hidden;
  background: linear-gradient(
      90deg,
      #f0f0f0 25%,
      #e0e0e0 37%,
      #f0f0f0 63%
  );
  background-size: 400% 100%;
  animation: shimmer 3s linear infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.typing-dots {
  display: inline-flex;
  align-items: flex-end;
  margin-right: 8px;
  height: 12px;
}

.dot {
  width: 6px;
  height: 6px;
  margin: 0 2px;
  border-radius: 50%;
  background-color: #333;
  animation: fall 1s infinite ease-in-out;
  transform-origin: center bottom;
}

.dot:nth-child(1) {
  animation-delay: 0s;
}
.dot:nth-child(2) {
  animation-delay: 0.15s;
}
.dot:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes fall {
  0% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(4px);
    opacity: 1;
  }
  60% {
    transform: translateY(0);
    opacity: 0.6;
  }
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
}

.typing-text {
  display: inline-block;
}

.typing-cursor {
  display: inline-block;
  animation: blink 0.7s infinite;
  font-weight: bold;
}

@keyframes blink {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.footer {
  display: flex;
  flex-direction: column;
  padding: 10px;
  border-top: 1px solid #ccc;
  background-color: #fff;
}

.disclaimer {
  font-size: 11px;
  color: #666;
  text-align: center;
  margin-bottom: 8px;
  padding: 0 10px;
}

.disclaimer-toggle {
  background: none;
  border: none;
  color: #0073bb;
  font-size: 11px;
  padding: 0;
  margin-left: 4px;
  cursor: pointer;
  text-decoration: underline;
}

.input-field {
  display: flex;
  width: 100%;

  &[disabled] {
    pointer-events: none;
    cursor: not-allowed;
    background-color: #ccc;
  }
}

.textarea-wrapper {
  position: relative;
  flex-grow: 1;
}

.message-input {
  width: 100%;
  line-height: 1.5;
  padding: 10px 44px 10px 10px;
  border: 2px solid #ccc;
  border-radius: 0.5rem;
  transition: border-color 0.3s ease;
  font-size: 13px;
  background-color: transparent;
  resize: none;
  field-sizing: content;
  max-height: 100px;
  box-sizing: border-box;

  &:focus-visible {
    outline-color: #0073bb;
  }

  &::placeholder {
    color: #888;
  }

  &[disabled] {
    background-color: #eaeaea;
    cursor: not-allowed;
  }
}

.send-button {
  position: absolute;
  right: 10px;
  bottom: 10px;
  color: #ccc;
  height: 32px;
  width: 32px;
  cursor: pointer;
  border-radius: 5px;

  &:hover {
    border-color: #0073bb;
  }

  &[disabled] {
    background-color: #eaeaea;
    cursor: not-allowed;
  }
}

::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #e7e4e4;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.visually-hidden {
  display: none;
}

.conversation-starters {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
}

.conversation-starter-btn {
  background-color: #fff;
  border: 1px solid #006db6;
  color: #006db6;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:hover {
    background-color: #006db6;
    color: #fff;
  }
}

.quick-actions-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-start;
  width: 45px;
  height: 45px;
  background-color: #eff6fb;
  border: none;
  cursor: pointer;
  color: #24467a;
  padding: 10px;
  margin-right: 10px;

  &:hover {
    background-color: #d6eaf5;
    color: #24467a;
  }
}

.quick-actions-slide-enter-active,
.quick-actions-slide-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  max-height: 100px;
  overflow: hidden;
}

.quick-actions-slide-enter,
.quick-actions-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.quick-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 10px;
}

.quick-action-btn {
  display: flex;
  align-items: center;
  justify-content: left;
  gap: 10px;
  padding: 10px;
  background-color: #eff6fb;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 600;
  color: #24467a;
  text-decoration: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #d6eaf5;
    color: #24467a;
    text-decoration: none;
  }
}

.message-timestamp {
  font-size: 10px;
  color: #999;
  margin-top: 3px;
  display: block;
  align-self: flex-start;

  &--right {
    align-self: flex-end;
    text-align: right;
  }
}

@media (max-width: 480px) {
  .floating-button {
    bottom: 10px;
    right: 10px;
    font-size: 14px;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .chatbot {
    width: calc(100% - 20px);
    height: calc(100% - 20px);
    bottom: 10px;
    right: 10px;
    border-radius: 8px;
    animation: fadeInFromBottom 0.3s ease-out;
  }

  .message {
    font-size: 14px;
  }

  .message-input {
    font-size: 16px;
  }

  .conversation-starter-btn {
    font-size: 14px;
  }

  .quick-action-btn {
    font-size: 14px;
  }
}

@keyframes fadeInFromBottom {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>

<style lang="scss">
.message.response {
  ol, ul {
    margin-top: 0;
    margin-bottom: 1rem;
    padding-left: 2rem;
  }
}
</style>
